import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';

interface MapBoundsAdjusterProps {
  perimetros: IPerimetro[];
  selectedPerimetroId: number | null;
  initialCenter: [number, number]; // Pass the initial center as a fallback
  initialZoom: number; // Pass the initial zoom
}
interface IPerimetro {
    Id: number;
    IdPlace: number;
    Name: string;
    GeoLocation: any;
}

const MapBoundsAdjuster: React.FC<MapBoundsAdjusterProps> = ({
  perimetros,
  selectedPerimetroId,
  initialCenter,
  initialZoom
}) => {
  const map = useMap();

  useEffect(() => {
    let targetBounds: LatLngBounds | null = null;
    let logMessage = "";

    // --- Lógica para determinar o bounds (exatamente como estava) ---
    if (selectedPerimetroId !== null) {
      logMessage = `MapBoundsAdjuster: Focando no ID: ${selectedPerimetroId}...`;
      const selectedPerimetro = perimetros.find(p => p.Id === selectedPerimetroId);

      if (selectedPerimetro?.GeoLocation) {
        try {
          const selectedFeature = { type: "Feature" as const, geometry: selectedPerimetro.GeoLocation, properties: {} };
          const selectedLayer = L.geoJSON(selectedFeature);
          const bounds = selectedLayer.getBounds();
          if (bounds.isValid()) {
            targetBounds = bounds;
            logMessage += " Bounds calculados.";
          } else {
            logMessage += " Bounds inválidos para o perímetro selecionado.";
          }
        } catch (e) {
          logMessage += ` Erro ao processar geometria: ${e}`;
        }
      } else {
        logMessage += " Perímetro selecionado não encontrado.";
      }
    }

    if (!targetBounds && perimetros && perimetros.length > 0) {
      logMessage = "MapBoundsAdjuster: Focando em todos os perímetros...";
      const validFeatures = perimetros
        .filter(p => p.GeoLocation && (p.GeoLocation.type === "Polygon" || p.GeoLocation.type === "MultiPolygon") && p.GeoLocation.coordinates)
        .map(p => ({
          type: "Feature" as const,
          geometry: p.GeoLocation,
          properties: {}
        }));

      if (validFeatures.length > 0) {
        const featureCollection: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features: validFeatures
        };
        const geoJsonLayer = L.geoJSON(featureCollection);
        const bounds = geoJsonLayer.getBounds();
        if (bounds.isValid()) {
          targetBounds = bounds;
          logMessage += ` Bounds calculados para ${validFeatures.length} perímetros.`;
        } else {
          logMessage += " Bounds inválidos para o conjunto de perímetros.";
        }
      } else {
        logMessage += " Nenhum perímetro válido encontrado.";
      }
    }

    // --- Aplicar o bounds e ADICIONAR LÓGICA DE POPUP ---
    console.log(logMessage);
    if (targetBounds && targetBounds.isValid()) {
      const mapSize = map.getSize();
      if (mapSize && mapSize.x > 0 && mapSize.y > 0) {
        
        // *** INÍCIO DA NOVA LÓGICA ***

        // 1. Define a função que abrirá o popup
        const openSelectedPopup = () => {
          map.off('moveend', openSelectedPopup); // Remove o ouvinte para não disparar novamente

          if (selectedPerimetroId === null) return; // Só abre se algo estiver selecionado

          // Itera sobre as camadas do mapa para encontrar o perímetro selecionado
          map.eachLayer(layer => {
            // Verifica se é uma camada GeoJSON (que tem a sub-função 'eachLayer')
            // e não é uma feature simples (que teria a propriedade 'feature')
            if (typeof (layer as any).eachLayer === 'function' && (layer as any).feature === undefined) {
              
              // Itera sobre as sub-camadas (os polígonos individuais)
              (layer as L.GeoJSON).eachLayer(subLayer => {
                const feature = (subLayer as any).feature;
                // Se a propriedade ID da feature bate com o ID selecionado
                if (feature && feature.properties && feature.properties.Id === selectedPerimetroId) {
                  // Abre o popup que foi VINCULADO no 'onEachPerimetro'
                  (subLayer as L.Path).openPopup();
                }
              });
            }
          });
        };

        // 2. Se estamos centralizando em um perímetro *específico*,
        //    registra um ouvinte para 'moveend' que chamará a função de abrir popup.
        if (selectedPerimetroId !== null) {
          map.once('moveend', openSelectedPopup);
        }

        // *** FIM DA NOVA LÓGICA ***

        // 3. Finalmente, chama o fitBounds (que iniciará o movimento)
        map.fitBounds(targetBounds, { padding: [50, 50] });
        console.log("MapBoundsAdjuster: Mapa ajustado para:", targetBounds);

      } else {
        console.warn("MapBoundsAdjuster: Tamanho do mapa inválido, não foi possível ajustar bounds.");
      }
    } else if (perimetros && perimetros.length === 0 && selectedPerimetroId === null) {
      console.log("MapBoundsAdjuster: Sem perímetros, usando visão inicial.");
      // map.setView(initialCenter, initialZoom); // Opcional
    } else {
      console.log("MapBoundsAdjuster: Nenhuns bounds válidos para ajustar.");
    }
  }, [perimetros, selectedPerimetroId, map, initialCenter, initialZoom]);

  return null;
};

export default MapBoundsAdjuster; // Export if in a separate file